import { useTheme } from '@automata/mui-theme';
import { alpha } from '@mui/material/styles';
import { Group } from '@visx/group';
import { scaleBand, scaleLinear, scaleOrdinal } from 'd3-scale';
import { useEffect, useMemo, useState } from 'react';

import Bars from './Bars';
import { getDomainXObject, getPaddedScale } from './helpers';
import { createRunProgressLabwareCentric } from './runProgressLabwareCentric';
import { AxisX } from '../../components/AxisX';
import { AxisY } from '../../components/AxisY';
import BrushX from '../../components/BrushX';
import BrushY from '../../components/BrushY';
import { Grid } from '../../components/Grid';
import Pattern1 from '../../components/Pattern1';
import { Playhead } from '../../components/Playhead';
import TooltipBoundsRect from '../../components/Tooltip/TooltipBoundsRect';
import ViewBorderLine from '../../components/ViewBorderLine';
import ZebraStripes from '../../components/ZebraStripes';
import { RunVisView, RunVisualizationData } from '../../types';

const LabwareView: RunVisView<RunVisualizationData> = ({
  runProgressData,
  width,
  brushHeight,
  viewHeight,
  elapsedTimeSeconds,
  menuOptions,
}) => {
  const theme = useTheme();
  const labwareData = useMemo(
    () => createRunProgressLabwareCentric(runProgressData.task_progress),
    [runProgressData.task_progress]
  );

  const axisYWidth = width * 0.2;
  const brushYWidth = 15;

  const domainXObject = getDomainXObject(labwareData);
  const domainYStatic = useMemo(() => Object.keys(labwareData), [labwareData]);

  const domainXStatic: [number, number] = useMemo(
    () => [0, domainXObject.max],
    [domainXObject.max]
  );
  const [domainXVariable, setDomainXVariable] = useState(domainXStatic);
  const [domainYVariable, setDomainYVariable] = useState(domainYStatic);

  useEffect(() => {
    setDomainXVariable((prev) => {
      // maintain x-axis pan/zoom when new data is added
      const low = Math.min(prev[0], domainXStatic[0]);
      const high = Math.max(prev[1], domainXStatic[1]);
      return [low, high];
    });
  }, [domainXStatic]);

  // update domainYVariable when task_progress data changes
  useEffect(() => {
    setDomainYVariable((prev) => {
      // maintain y-axis pan/zoom if there are common items in new data
      const intersectedItems = domainYStatic.filter((item) =>
        prev.includes(item)
      );
      if (intersectedItems.length === 0) return domainYStatic;
      return intersectedItems;
    });
  }, [domainYStatic]);

  const rangeXBrush = [0, width];
  const scaleXBrush = getPaddedScale(
    scaleLinear(domainXStatic, rangeXBrush),
    30
  );

  const rangeXView = [axisYWidth, width - brushYWidth];
  const scaleXView = scaleLinear(domainXVariable, rangeXView);
  const axisXHeight = 40;

  const contentHeight = viewHeight - axisXHeight;
  const viewPaddingBottom = 20;
  const yScalePadAmount = 0.2;
  const rangeY: [number, number] = [
    axisXHeight,
    contentHeight + axisXHeight - viewPaddingBottom,
  ];
  const scaleYVariable = scaleBand(domainYVariable, rangeY);
  const scaleYVariablePadded = scaleYVariable
    .copy()
    .paddingInner(yScalePadAmount)
    .paddingOuter(yScalePadAmount / 2);

  const brushPaddingY = 5;
  const brushHeightPadded = brushHeight - brushPaddingY * 2;
  const rangeYBrush = [brushPaddingY, brushHeight - brushPaddingY];
  const scaleYBrush = scaleBand(domainYStatic, rangeYBrush)
    .paddingInner(yScalePadAmount)
    .paddingOuter(yScalePadAmount * 2.5);

  const rangeColor: string[] = [];
  const uniqueNoLabwareTools = new Set<string>();

  Object.values(labwareData).forEach((item) => {
    item.tasks.forEach(({ task }) => {
      rangeColor.push(task.instrument.id);
    });
  });

  // Collect unique tools for no_labware tasks, if they exist
  if (labwareData?.no_labware?.tasks) {
    for (const taskProgress of labwareData.no_labware.tasks) {
      const task = taskProgress.task;
      if ('labware_in' in task && task.labware_in?.[0]?.id === 'no_labware') {
        uniqueNoLabwareTools.add(task.instrument.id || 'no_labware');
      }
    }
  }

  const domainColor = [
    theme.palette.darkBlue.main,
    theme.palette.plateGreen.main,
    theme.palette.marineGreen.main,
    theme.palette.winterGreen.main,
    theme.palette.swimBlue.main,
    theme.palette.greyBlue.main,
    theme.palette.skyBlue.main,
    theme.palette.softPink.main,
    theme.palette.cerise.main,
    theme.palette.terracotta.main,
    theme.palette.candyPink.main,
    theme.palette.spotRed.main,
  ];

  // Adjust the domainColor to have lower opacity for levels of no_labware
  const adjustedDomainColor = domainColor.map((color, index) =>
    index < uniqueNoLabwareTools.size ? alpha(color, 0.4) : color
  );

  // Ensure adjustedDomainColor has enough colors to match rangeColor
  while (adjustedDomainColor.length < rangeColor.length) {
    if (domainColor.length === 0)
      throw new Error('No base domain colors defined.');
    const nextIndex = adjustedDomainColor.length;
    const baseColor = domainColor[nextIndex % domainColor.length] ?? '#000';
    const adjustedColor =
      nextIndex < uniqueNoLabwareTools.size ? alpha(baseColor, 0.4) : baseColor;

    adjustedDomainColor.push(adjustedColor);
  }

  const scaleColor = scaleOrdinal(rangeColor, adjustedDomainColor);

  const shouldShowPlayhead = elapsedTimeSeconds !== undefined;
  const playheadXPos = scaleXView(elapsedTimeSeconds || 0);

  return (
    <Group id="labware-view">
      <Pattern1 />
      <g data-testid="view">
        <TooltipBoundsRect
          x={axisYWidth}
          y={axisXHeight}
          width={width - axisYWidth - brushYWidth}
          height={viewHeight - viewPaddingBottom - axisXHeight}
        />

        <AxisX
          scale={scaleXView}
          width={width}
          height={axisXHeight}
          margin={{ left: 0, top: 0 }}
        />

        <Group left={axisYWidth}>
          <ZebraStripes
            width={width - axisYWidth}
            scaleY={scaleYVariable}
            domainY={domainYVariable}
          />
        </Group>

        <Group top={axisXHeight}>
          <Grid
            width={width}
            height={rangeY[1] - axisXHeight}
            scaleX={scaleXView}
          />
        </Group>

        <Bars
          labwareData={labwareData}
          scaleColor={scaleColor}
          scaleX={scaleXView}
          scaleY={scaleYVariablePadded}
        />

        <ZebraStripes
          width={axisYWidth}
          scaleY={scaleYVariable}
          domainY={domainYVariable}
        />
        <AxisY
          values={domainYVariable.map((id) => ({
            id,
            label: labwareData[id]?.label ?? '',
          }))}
          scaleY={scaleYVariablePadded}
          width={axisYWidth}
          menuOptions={menuOptions}
        />

        {shouldShowPlayhead && playheadXPos > axisYWidth && (
          <Playhead x={playheadXPos} height={viewHeight - viewPaddingBottom} />
        )}
      </g>
      <Group data-testid="brush-x" className="brush-x" top={viewHeight}>
        <rect
          width={Math.max(width, 0)}
          height={Math.max(brushHeight, 0)}
          fill={theme.palette.neutral[200]}
          rx={2}
        />
        <Bars
          labwareData={labwareData}
          scaleColor={scaleColor}
          scaleX={scaleXBrush}
          scaleY={scaleYBrush}
          isBrush
        />
        {shouldShowPlayhead && (
          <rect
            data-testid="playhead-rect-brush"
            width={2}
            height={Math.max(brushHeight, 0)}
            fill={theme.palette.accent2[400]}
            x={scaleXBrush(elapsedTimeSeconds)}
            y={0}
          />
        )}
        <BrushX
          top={brushPaddingY}
          height={brushHeightPadded}
          width={width}
          scaleX={scaleXBrush}
          scaleY={scaleYBrush}
          onChange={(domain) => {
            if (!domain) return;
            const low = Math.max(domain.x0, 0);
            const high = Math.max(domain.x1, 0);
            setDomainXVariable([low, high]);
          }}
        />
      </Group>
      <g data-testid="brush-y">
        <BrushY
          domain={domainYStatic}
          height={viewHeight - viewPaddingBottom}
          width={brushYWidth}
          x={width - brushYWidth}
          y={axisXHeight}
          onChange={setDomainYVariable}
        />

        <ViewBorderLine
          x1={axisYWidth}
          y1={rangeY[0]}
          x2={axisYWidth}
          y2={rangeY[1]}
        />

        <ViewBorderLine
          x1={axisYWidth}
          y1={axisXHeight}
          x2={width}
          y2={axisXHeight}
        />
      </g>
    </Group>
  );
};

export default LabwareView;
